import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, currency, daysUntil, computeMatch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Calendar, DollarSign, Building2, CheckCircle2, Sparkles, PlayCircle, Timer } from "lucide-react";
import { Badge } from "../components/ui/badge";

export default function GrantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [grant, setGrant] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get(`/api/grants/${id}`).then((r) => setGrant(r.data));
  }, [id]);

  const startApplication = async () => {
    if (!user) return nav("/login", { state: { from: `/grants/${id}` } });
    setStarting(true);
    try {
      const { data } = await api.post("/api/applications/", { grant_id: id, auto_submit: true });
      toast.success("Application started. Let's draft documents.");
      nav(`/applications/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start application");
    } finally { setStarting(false); }
  };

  if (!grant) return <div className="p-16 text-center text-neutral-500">Loading grant…</div>;

  const d = daysUntil(grant.deadline);
  const match = computeMatch(grant, user);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <Link to="/grants" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-6" data-testid="back-to-grants">
          <ArrowLeft size={16} /> Back to grants
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Badge variant="outline" className="mb-3">{grant.category}</Badge>
            <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight" data-testid="grant-title">{grant.title}</h1>
            <div className="mt-3 text-neutral-600 flex items-center gap-2"><Building2 size={16} /> {grant.agency}</div>
            <p className="mt-6 text-neutral-800 leading-relaxed text-lg">{grant.description}</p>

            <div className="mt-8">
              <div className="overline mb-3">REQUIRED DOCUMENTS</div>
              <ul className="grid sm:grid-cols-2 gap-2">
                {(grant.requirements || []).map((r) => (
                  <li key={r} className="flex items-center gap-2 border border-neutral-200 rounded-lg p-3">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    <span className="text-sm">{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="border border-neutral-200 rounded-lg p-4">
                <div className="overline">Eligibility</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {grant.eligibility.map((e) => <Badge key={e} variant="secondary" className="capitalize">{e.replace("_", " ")}</Badge>)}
                </div>
              </div>
              <div className="border border-neutral-200 rounded-lg p-4">
                <div className="overline">Level</div>
                <div className="mt-2 capitalize font-medium">{grant.level}{grant.state ? ` · ${grant.state}` : ""}</div>
              </div>
              <div className="border border-neutral-200 rounded-lg p-4">
                <div className="overline">Industries</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(grant.industries || []).map((i) => <Badge key={i} variant="secondary">{i}</Badge>)}
                </div>
              </div>
            </div>
          </div>

          {/* Sticky action card */}
          <aside className="lg:col-span-1">
            <div className="gf-card p-6 lg:sticky lg:top-24" data-testid="grant-action-card">
              <div className="flex items-center justify-between">
                <div className="overline">Match score</div>
                <div className="font-display text-3xl font-bold">{match}%</div>
              </div>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-neutral-100 flex items-center justify-center"><DollarSign size={16} /></div>
                  <div>
                    <div className="text-xs text-neutral-500">Amount</div>
                    <div className="font-semibold">{currency(grant.amount_min)} – {currency(grant.amount_max)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center ${d < 14 ? "bg-red-100 text-red-700" : "bg-neutral-100 text-neutral-700"}`}><Timer size={16} /></div>
                  <div>
                    <div className="text-xs text-neutral-500">Deadline</div>
                    <div className={`font-semibold ${d < 14 ? "text-red-700" : ""}`}>{new Date(grant.deadline).toLocaleDateString()} · {d}d left</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-neutral-100 flex items-center justify-center"><Calendar size={16} /></div>
                  <div>
                    <div className="text-xs text-neutral-500">Documents needed</div>
                    <div className="font-semibold">{(grant.requirements || []).length} items</div>
                  </div>
                </div>
              </div>
              <button onClick={startApplication} disabled={starting}
                className="mt-6 btn-primary w-full justify-center" data-testid="start-application">
                <PlayCircle size={16} /> {starting ? "Starting…" : "Start application"}
              </button>
              <Link to={user?.subscription_status === "active" ? `/generate?grant=${id}` : "/pricing"} className="mt-3 btn-accent w-full justify-center" data-testid="draft-with-ai">
                <Sparkles size={16} /> Draft docs with AI
              </Link>
              <p className="mt-3 text-xs text-neutral-500 text-center">Auto-submit reminders on by default. Change anytime.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
